import React, { memo } from 'react';
import { ReactFlow } from '@xyflow/react';
import { Loader } from '@mantine/core';
import styled from 'styled-components';
import '@xyflow/react/dist/style.css';

import { CapChartContent } from './CapChartContent';
import { useCapChartController } from './useCapChartController';
import {
	NODE_HEIGHT,
	NODE_WIDTH,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/Nodes.tsx';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { generatePath, useParams } from 'react-router-dom';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import {
	useTranslationCapChart,
	useTranslationCapTable,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

const StyledReactFlow = styled(ReactFlow)`
	.react-flow__node {
		display: flex;
		align-items: center;
		justify-content: center;
		width: ${NODE_WIDTH}px;
		height: ${NODE_HEIGHT}px;

		&.hovered {
			z-index: 1003 !important;

			&.debt div[data-handleid='source-debt'] {
				background-color: ${({ theme }) => theme.v3.colors.blue[6]} !important;
				transition: background-color 0.2s ease-in-out;
			}
		}
	}

	.react-flow__handle {
		height: 0;
		width: 0;
		min-width: 0;
		min-height: 0;
		border: none;
	}

	.react-flow__edges svg {
		overflow: visible !important;
	}

	.react-flow__edges svg:has(.react-flow__edge.hovered),
	.react-flow__edges svg:hover {
		z-index: 1002 !important;
	}

	.react-flow__edge.hovered:hover,
	.react-flow__edge.hovered {
		path {
			stroke: ${({ theme }) => theme.v3.colors.blue[6]} !important;
			transition: stroke 0.2s ease-in-out;
		}
	}

	/* !important beats the label's inline Mantine style props. */
	.edge-label.hovered {
		z-index: 1005 !important;
		border-color: ${({ theme }) => theme.v3.colors.blue[6]} !important;
		color: ${({ theme }) => theme.v3.colors.blue[6]} !important;
	}
`;

const fitViewOptions = {
	padding: 0.1,
	duration: 200,
};

const CapChart = () => {
	const tCapTable = useTranslationCapTable();
	const tCapChart = useTranslationCapChart();
	const { dealId } = useParams();

	const {
		nodes,
		edges,
		nodeTypes,
		edgeTypes,
		isDataLoaded,
		onEdgeMouseEnter,
		onEdgeMouseLeave,
		onNodesChange,
		onEdgesChange,
		onConnect,
		connectionLineType,
		onSave,
		onReset,
	} = useCapChartController();

	// this can move to CapChartPage when in page feature flag is removed
	useBreadcrumbs([
		{
			name: tCapTable('title'),
			url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.CAP_TABLE}`, {
				dealId: dealId ?? '',
			}),
		},
		{
			name: tCapChart('chart_title'),
			url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.CAP_CHART}`, { dealId: dealId ?? '' }),
		},
	]);

	return isDataLoaded ? (
		<StyledReactFlow
			snapToGrid
			onEdgeMouseEnter={onEdgeMouseEnter}
			onEdgeMouseLeave={onEdgeMouseLeave}
			snapGrid={[50, 50]}
			nodes={nodes}
			edges={edges}
			nodeTypes={nodeTypes}
			edgeTypes={edgeTypes}
			onNodesChange={onNodesChange}
			onEdgesChange={onEdgesChange}
			onConnect={onConnect}
			connectionLineType={connectionLineType}
			fitView
			fitViewOptions={fitViewOptions}
			nodesDraggable
			elementsSelectable
			nodesConnectable={false}
		>
			<CapChartContent
				hasElements={!!nodes.length || !!edges.length}
				onSave={onSave}
				onReset={onReset}
			/>
		</StyledReactFlow>
	) : (
		<Loader />
	);
};

const Memoized = memo(CapChart);

export { Memoized as CapChart };
